import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Card,
  FormDialog,
  MuiBox,
  MuiTable,
  SvgIcon,
  SvgIconButton,
  Tooltip,
  Typography
} from '@kis-technologies/kis-ui';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import {
  TicketTemplate,
  actions as ticketTemplatesActions,
  selectors as ticketTemplatesSelectors
} from 'store/ticketTemplates';
import { KioskTemplateOverlayProp, TemplatePayload, Mode } from 'views/KiosksView/types';
import { ActionOverlay } from 'components/ActionOverlay';
import { getSortedList } from 'utils/stringFormat';
import { useKioskFormStyles } from '../styled';
import { TemplateForm } from './TemplateForm';

export const KioskTemplateOverlay = (props: KioskTemplateOverlayProp) => {
  const { canEdit, onClose } = props;
  const classes = useKioskFormStyles();
  const [
    getTicketTemplates,
    deleteTicketTemplate,
    createTicketTemplate,
    updateTicketTemplate,
    resetTicketTemplateError
  ] = useActions([
    ticketTemplatesActions.getTicketTemplates,
    ticketTemplatesActions.deleteTicketTemplate,
    ticketTemplatesActions.createTicketTemplate,
    ticketTemplatesActions.updateTicketTemplate,
    ticketTemplatesActions.resetError
  ]);

  const { error: ticketTemplateError, pending: ticketTemplatePending } = useSelector(
    ticketTemplatesSelectors.selectAction
  );
  const templates = useSelector(ticketTemplatesSelectors.selectTicketTemplates);

  const [selectedTemplate, setSelectedTemplate] = useState<TicketTemplate | null>(null);
  const [showCreateEdit, setShowCreateEdit] = useState(false);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const [actionType, setActionType] = useState<Mode | null>(null);
  const [sortColumn, setSortColumn] = useState<'name' | 'type'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [uiLoading, setUILoading] = useState(false);
  const [error, setError] = useState('');

  const sortedTemplates = useMemo(() => {
    if (!templates?.length) return [];

    const sorted = getSortedList(templates, sortColumn);
    return sortOrder === 'asc' ? sorted : [...sorted].reverse();
  }, [templates, sortColumn, sortOrder]);

  useEffect(() => {
    getTicketTemplates();
  }, [getTicketTemplates]);

  useEffect(() => {
    if (uiLoading && !ticketTemplatePending) {
      if (!ticketTemplateError) {
        setShowCreateEdit(false);
        setSelectedTemplate(null);
        setActionType(null);
        getTicketTemplates();
      }
      setUILoading(false);
    }
  }, [uiLoading, ticketTemplatePending, ticketTemplateError, getTicketTemplates]);

  useEffect(() => {
    if (ticketTemplateError) {
      setError(ticketTemplateError);
      setShowCreateEdit(false);
      setUILoading(false);
    }
  }, [ticketTemplateError]);

  const handleSubmit = (data: TemplatePayload) => {
    setUILoading(true);
    setError('');

    if (actionType === 'edit') {
      updateTicketTemplate(data);
    } else {
      createTicketTemplate(data);
    }
  };

  const handleEdit = (template: TicketTemplate) => {
    setError('');
    setSelectedTemplate(template);
    setShowCreateEdit(true);
    setActionType('edit');
  };

  const handleDelete = () => {
    if (selectedTemplate) {
      deleteTicketTemplate({ id: selectedTemplate?.id });
      setSelectedTemplate(null);
      setShowDeleteOverlay(false);
      getTicketTemplates();
    }
  };

  const handleOnFormClose = () => {
    setShowCreateEdit(false);
    setActionType(null);
    getTicketTemplates();
  };

  const handleSort = (column: 'name' | 'type') => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const SortArrow = ({ active }: { active: boolean }) =>
    active ? (
      <SvgIcon name={sortOrder === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
    ) : null;

  return (
    <FormDialog
      open
      title="Templates"
      confirmButtonText={'Close'}
      confirmButtonAction={onClose}
      headerButtonAction={() => {
        if (!canEdit) return;
        setError('');
        setShowCreateEdit(true);
        setActionType('create');
      }}
      headerButtonText="Create Template"
    >
      <MuiBox mb="1rem" mt="14px">
        <Card hasBorderRadius raised fullHeight>
          <Box flexOverride="1">
            <MuiTable.TableContainer id="kioskDetailsTable">
              <MuiTable.Table stickyHeader>
                <MuiTable.TableHead>
                  <MuiTable.TableRow>
                    <MuiTable.TableCell
                      className={classes.kioskDetailsHeaders}
                      sx={{ width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => handleSort('name')}
                    >
                      Name <SortArrow active={sortColumn === 'name'} />
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.kioskDetailsHeaders}
                      sx={{ width: '220px', whiteSpace: 'nowrap', cursor: 'pointer' }}
                      onClick={() => handleSort('type')}
                    >
                      Type <SortArrow active={sortColumn === 'type'} />
                    </MuiTable.TableCell>
                    <MuiTable.TableCell
                      className={classes.kioskDetailsHeaders}
                      sx={{ cursor: 'default', textAlign: 'center' }}
                    >
                      Actions
                    </MuiTable.TableCell>
                  </MuiTable.TableRow>
                </MuiTable.TableHead>
                <MuiTable.TableBody>
                  {sortedTemplates.map((template: TicketTemplate) => (
                    <MuiTable.TableRow key={template.id}>
                      <MuiTable.TableCell>{template.name}</MuiTable.TableCell>
                      <MuiTable.TableCell>{template.type}</MuiTable.TableCell>
                      <MuiTable.TableCell align="center">
                        {canEdit && (
                          <MuiBox display="flex" gap="10px">
                            <Tooltip title="Edit Template" arrow placement="top" verticalOffset={-8}>
                              <SvgIconButton onClick={() => handleEdit(template)}>
                                <SvgIcon name="edit_circle" />
                              </SvgIconButton>
                            </Tooltip>
                            <Tooltip title="Delete Template" arrow placement="top" verticalOffset={-8}>
                              <SvgIconButton
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setShowDeleteOverlay(true);
                                }}
                              >
                                <SvgIcon name="exit_circle" />
                              </SvgIconButton>
                            </Tooltip>
                          </MuiBox>
                        )}
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  ))}
                </MuiTable.TableBody>
              </MuiTable.Table>
            </MuiTable.TableContainer>
          </Box>
        </Card>
        {showDeleteOverlay && (
          <FormDialog
            open={showDeleteOverlay}
            confirmButtonAction={handleDelete}
            confirmButtonText="Delete"
            cancelButtonAction={() => {
              setSelectedTemplate(null);
              setShowDeleteOverlay(false);
            }}
            cancelButtonText="Cancel"
          >
            <MuiBox>
              <Typography fontSize={24}>Are you sure you want to delete this template?</Typography>
            </MuiBox>
          </FormDialog>
        )}
        {showCreateEdit && actionType && (
          <TemplateForm
            mode={actionType}
            onClose={handleOnFormClose}
            onSubmit={handleSubmit}
            initialData={selectedTemplate || undefined}
            error={error}
          />
        )}
      </MuiBox>
      {ticketTemplateError && (
        <ActionOverlay
          title={
            <MuiBox className={classes.errorDeleteHeading}>
              <Typography className={classes.errorDeleteHeader}>{ticketTemplateError}</Typography>
            </MuiBox>
          }
          confirmText="Close"
          onConfirm={resetTicketTemplateError}
          width={800}
          height={350}
          appearance="error"
        />
      )}
    </FormDialog>
  );
};
